/**
 * Key/value table editor for `dict[str, str]` config fields (ADR-0009).
 *
 * Replaces the generic string control for the http_request / http_sink
 * dict fields — `headers`, `query_parameters`, `body_fields` — which are
 * marked `x-tray-expression: template` but hold an object, not a string.
 * Before this they fell through to `ExpressionField`, which stringified
 * the object to `[object Object]`.
 *
 * Each row is a `{ key, value }` pair. Values are per-item `{{ }}`
 * interpolation templates (the backend renders each dict value via
 * `resolve_config`), so the value input accepts free text incl. braces —
 * no per-value Monaco editor (kept deliberately lightweight; a single
 * `{{ expr }}` still works because the backend treats the whole value as
 * a template).
 *
 * The component is **controlled**: it owns no canonical state, it just
 * projects the `Record<string,string>` to rows and emits a rebuilt record
 * on every edit. Empty keys are dropped on emit so a half-typed row never
 * persists a `"": ""` entry.
 */
import { Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface KeyValueEditorProps {
  id?: string;
  value: Record<string, string> | null | undefined;
  onChange: (v: Record<string, string>) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

interface Row {
  key: string;
  value: string;
}

/** Project a record into ordered rows for editing. */
function toRows(value: Record<string, string> | null | undefined): Row[] {
  if (!value || typeof value !== "object") return [];
  return Object.entries(value).map(([key, v]) => ({
    key,
    value: typeof v === "string" ? v : String(v ?? ""),
  }));
}

/** Rebuild a record from rows, dropping rows with an empty key. Later
 *  duplicate keys win (last write), matching object semantics. */
function toRecord(rows: Row[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = r.key.trim();
    if (k) out[k] = r.value;
  }
  return out;
}

export function KeyValueEditor({
  id,
  value,
  onChange,
  keyPlaceholder = "name",
  valuePlaceholder = "value (supports {{ }})",
}: KeyValueEditorProps) {
  // Local row state so a half-typed key doesn't vanish mid-edit (rebuilding
  // straight from the record would drop an empty-key row on every keystroke).
  const [rows, setRows] = React.useState<Row[]>(() => toRows(value));

  // Re-sync when the upstream value changes identity (node switch, discard,
  // load) but not on our own emits — compare the canonical projection.
  const lastEmitted = React.useRef<string>(JSON.stringify(toRecord(rows)));
  React.useEffect(() => {
    const incoming = JSON.stringify(value ?? {});
    if (incoming !== lastEmitted.current) {
      setRows(toRows(value));
      lastEmitted.current = incoming;
    }
  }, [value]);

  const commit = (next: Row[]) => {
    setRows(next);
    const record = toRecord(next);
    lastEmitted.current = JSON.stringify(record);
    onChange(record);
  };

  const updateRow = (idx: number, patch: Partial<Row>) =>
    commit(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeRow = (idx: number) => commit(rows.filter((_, i) => i !== idx));

  const addRow = () => commit([...rows, { key: "", value: "" }]);

  return (
    <div id={id} className="space-y-1.5">
      {rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((row, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <Input
                aria-label={`key ${idx + 1}`}
                className="flex-1 bg-background font-mono text-xs"
                value={row.key}
                placeholder={keyPlaceholder}
                onChange={(e) => updateRow(idx, { key: e.target.value })}
              />
              <Input
                aria-label={`value ${idx + 1}`}
                className="flex-1 bg-background font-mono text-xs"
                value={row.value}
                placeholder={valuePlaceholder}
                onChange={(e) => updateRow(idx, { value: e.target.value })}
              />
              <button
                type="button"
                aria-label={`remove row ${idx + 1}`}
                className="rounded p-1 hover:bg-elevated"
                onClick={() => removeRow(idx)}
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-xs"
        onClick={addRow}
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
        Add
      </Button>
    </div>
  );
}
